import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentSteps, PaymentTitle, Toast, useSciDomain } from 'components';
import { ToastContext } from 'context';
import { OrderStatus, SciView } from 'enums';
import { useOrderContext, useToastState } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { useOrderStatusView } from 'pages/Order/hooks/useOrderStatusView';
import { OrderLayoutProps } from 'types';

export const DefaultLayout: React.FC<OrderLayoutProps> = ({
  children,
}: OrderLayoutProps) => {
  const { data: sciDomainConfig } = useSciDomain();
  const {
    order,
    status,
    requisitesBankName: bankName,
    paymentTypeFlags: { isCustomNspkForm, isEcom },
    paymentForm: { isCustomerDataConfirmed: cardConfirmed },
  } = useOrderContext();

  const shopName = order?.shop?.name;

  const { innerStatus, stepIndex } = useOrderStatusView({
    order,
    status,
    cardConfirmed,
  });

  const { t: tToast } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sq_toast',
  });

  const { toastOpen, toastText, showToast } = useToastState();

  const showCopiedToast = useCallback(
    () => showToast(tToast('copied')),
    [showToast, tToast],
  );

  const showCopyFailedToast = useCallback(
    () => showToast(tToast('copy_failed')),
    [showToast, tToast],
  );

  // Ecom is a fixed 2-step flow (1 = card data, 2 = confirmation code / gateway); its statuses do not map
  // onto the generic 5-step P2P stepper, so drive both the count and the active step directly.
  const ecomStep = status === OrderStatus.New ? 1 : 2;

  return (
    <ToastContext.Provider value={{ showCopiedToast, showCopyFailedToast }}>
      <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-w-full tw-max-w-sm tw-mx-auto md:tw-max-w-7xl">
        {toastOpen && <Toast text={toastText} />}
        {sciDomainConfig?.view !== SciView.Compact && (
          <div className="tw-w-full tw-mb-12 md:tw-mb-12 md:tw-w-auto md:tw-max-w-full">
            <PaymentSteps
              stepsCount={isEcom ? 2 : 5}
              activeStep={isEcom ? ecomStep : stepIndex[innerStatus]}
            />
          </div>
        )}
        {!isCustomNspkForm && !isEcom && (
          <div className="tw-w-full tw-mb-8 md:tw-mb-24">
            <PaymentTitle
              stepName={innerStatus}
              storeName={shopName}
              bankName={bankName}
              paymentType={order.payment.type}
              sciCustomTradeMethodsEnabled={
                order?.shop?.sciCustomTradeMethodsEnabled
              }
            />
          </div>
        )}
        {children}
      </div>
    </ToastContext.Provider>
  );
};
